import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { doc, setDoc } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import type { TimelineElement, MarkdownSyncMetadata } from '../../context/TimelineContext'
import type { useDashboard } from '../../context/useDashboard'
import type { useAuth } from '../../context/useAuth'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import { useChecklistLockState } from '../../hooks/useChecklistLockState'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import {
  BOARD_PADDING,
  GATE_DIAMETER,
  NODE_BODY_HEIGHT,
  NODE_HEIGHT,
  NODE_WIDTH,
} from '../../services/canvasLayoutConstants'
import {
  isGateNode,
  CHECKLIST_BOTTOM_CLEARANCE,
  EMPTY_BOARD_MIN_WIDTH,
  EMPTY_BOARD_HEIGHT,
  MIN_VIEWPORT_WIDTH,
  getNodeDimensions,
  normalizeBoardItem,
} from './boardItem'
import { getCursorColor } from './canvasMisc'

export function useVisionboardAndMiniMap({
  elements,
  viewportSize,
  displayNodes,
  boardRef,
  setViewportSize,
  scrollFrameRef,
  setCanvasTransform,
  panCanvasToCoordinates,
  nodesById,
  setSelectedNodeId,
  boardScale,
  pendingOpenChecklistItemIdRef,
  elementById,
  externalWorkspaceProgressByToken,
  currentUser,
  latestCursorPayloadRef,
  item,
  currentDisplayName,
  setFlashMessage,
  markdownMetadata,
  setMarkdownMetadata,
  recurringLens,
}: {
  elements: TimelineElement[]
  viewportSize: { width: number; height: number; scrollLeft: number; scrollTop: number }
  displayNodes: BoardChecklistItem[]
  boardRef: MutableRefObject<HTMLDivElement | null>
  setViewportSize: Dispatch<SetStateAction<{ width: number; height: number; scrollLeft: number; scrollTop: number }>>
  scrollFrameRef: MutableRefObject<number | null>
  setCanvasTransform: (transform: { x: number; y: number; zoom: number }) => void
  panCanvasToCoordinates: (x: number, y: number) => void
  nodesById: Map<string, BoardChecklistItem>
  setSelectedNodeId: ReturnType<typeof useDashboard>['setSelectedChecklistNodeId']
  boardScale: number
  pendingOpenChecklistItemIdRef: MutableRefObject<string | null>
  elementById: Map<string, TimelineElement>
  externalWorkspaceProgressByToken: Map<string, number>
  currentUser: ReturnType<typeof useAuth>['currentUser']
  latestCursorPayloadRef: MutableRefObject<{ canvasX: number; canvasY: number; activeFocusedNodeId: string | null } | null>
  item: TimelineElement
  currentDisplayName: string
  setFlashMessage: Dispatch<SetStateAction<string | null>>
  markdownMetadata: MarkdownSyncMetadata
  setMarkdownMetadata: Dispatch<SetStateAction<MarkdownSyncMetadata>>
  recurringLens?: ReturnType<typeof useRecurringLens>
}) {
  const { todayIso, weekKey, timeZone } = useWorkspaceDay(item.workspaceId)
  const allNodes = useMemo(() => Array.from(nodesById.values()), [nodesById])
  const lockTargets = useMemo(() => {
    return displayNodes
      .filter((node) => node.nodeKind !== 'GATE_AND' && node.nodeKind !== 'GATE_OR')
      .map((node) => ({
        itemId: node.id,
        periodKey: recurringLens ? recurringLens.focusedKey : (node.checklistType === 'WEEKLY' ? weekKey : todayIso),
      }))
  }, [displayNodes, recurringLens, todayIso, weekKey])

  const lockMap = useChecklistLockState(allNodes, lockTargets, {
    workspaceId: item.workspaceId,
    timeZone,
  })
  // Visionboard shelf source. Initial population comes from checklist items
  // already attached to elements with `board === 'VISIONBOARD'`; thereafter
  // it's session-mutable so canvas → shelf and shelf → canvas drags can move
  // items in and out without round-tripping through Firestore.
  const initialVisionboardItems = useMemo(
    () => elements
      .filter((element) => element.board === 'VISIONBOARD')
      .flatMap((element) => (element.checklist ?? []).map((item, index) =>
        normalizeBoardItem(item.id, {
          ...item,
          parentContainerId: element.id,
          workspaceId: element.workspaceId,
          board: 'VISIONBOARD',
        }, index)
      )),
    [elements],
  )
  const [visionboardChecklistItems, setVisionboardChecklistItems] = useState<BoardChecklistItem[]>(initialVisionboardItems)
  const hydratedVisionboardRef = useRef(false)
  useEffect(() => {
    if (hydratedVisionboardRef.current) return
    if (initialVisionboardItems.length === 0) return
    hydratedVisionboardRef.current = true
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisionboardChecklistItems(initialVisionboardItems)
  }, [initialVisionboardItems])

  const boardMetrics = useMemo(() => {
    const viewportWidth = Math.max(MIN_VIEWPORT_WIDTH, viewportSize.width || EMPTY_BOARD_MIN_WIDTH)

    if (displayNodes.length === 0) {
      return {
        minX: -BOARD_PADDING,
        minY: -BOARD_PADDING,
        offsetX: BOARD_PADDING,
        offsetY: BOARD_PADDING,
        width: viewportWidth,
        height: Math.max(EMPTY_BOARD_HEIGHT, viewportSize.height),
      }
    }

    const minX = Math.min(...displayNodes.map((node) => node.position.x))
    const minY = Math.min(...displayNodes.map((node) => node.position.y))
    const maxX = Math.max(...displayNodes.map((node) => node.position.x + getNodeDimensions(node).width))
    const maxY = Math.max(...displayNodes.map((node) => node.position.y + getNodeDimensions(node).height))
    const checklistMaxY = Math.max(
      0,
      ...displayNodes.filter((node) => !isGateNode(node)).map((node) => node.position.y + NODE_HEIGHT),
    )
    const offsetX = Math.max(BOARD_PADDING - minX, BOARD_PADDING)
    const offsetY = Math.max(BOARD_PADDING - minY, BOARD_PADDING)

    return {
      minX,
      minY,
      offsetX,
      offsetY,
      width: Math.max(viewportWidth, maxX + offsetX + BOARD_PADDING),
      height: Math.max(
        EMPTY_BOARD_HEIGHT,
        viewportSize.height,
        maxY + offsetY + BOARD_PADDING,
        checklistMaxY + offsetY + CHECKLIST_BOTTOM_CLEARANCE,
      ),
    }
  }, [displayNodes, viewportSize.height, viewportSize.width])

  const miniMapItems = useMemo(() => displayNodes.map((node) => ({
    id: node.id,
    x: node.position.x + boardMetrics.offsetX + (getNodeDimensions(node).width / 2),
    y: node.position.y + boardMetrics.offsetY + ((isGateNode(node) ? GATE_DIAMETER : NODE_BODY_HEIGHT) / 2),
    isUrgentCrisisMode: node.isUrgentCrisisMode === true,
  })), [boardMetrics.offsetX, boardMetrics.offsetY, displayNodes])

  const syncCanvasTransformFromScroll = useCallback(() => {
    const board = boardRef.current
    if (!board) return
    const scrollLeft = board.scrollLeft
    const scrollTop = board.scrollTop

    setViewportSize((current) => ({
      ...current,
      scrollLeft,
      scrollTop,
    }))

    if (scrollFrameRef.current !== null) window.cancelAnimationFrame(scrollFrameRef.current)
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null
      setCanvasTransform({
        x: scrollLeft,
        y: scrollTop,
        zoom: 1,
      })
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: boardRef, scrollFrameRef, setViewportSize are useRef/useState identities passed from the Surface (stable)
  }, [setCanvasTransform])

  const handleMiniMapPan = useCallback((targetX: number, targetY: number) => {
    const board = boardRef.current
    if (!board) return

    const nextLeft = Math.max(0, targetX - (board.clientWidth / 2))
    const nextTop = Math.max(0, targetY - (board.clientHeight / 2))
    board.scrollTo({
      left: nextLeft,
      top: nextTop,
      behavior: 'smooth',
    })
    panCanvasToCoordinates(nextLeft, nextTop)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: boardRef are useRef/useState identities passed from the Surface (stable)
  }, [panCanvasToCoordinates])

  const openChecklistItemNode = useCallback((checklistItemId: string): boolean => {
    if (!checklistItemId) return false
    const node = nodesById.get(checklistItemId)
    if (!node || isGateNode(node)) return false

    setSelectedNodeId(checklistItemId)
    handleMiniMapPan(
      (node.position.x + boardMetrics.offsetX + (NODE_WIDTH / 2)) * boardScale,
      (node.position.y + boardMetrics.offsetY + (NODE_HEIGHT / 2)) * boardScale,
    )
    return true
  }, [boardMetrics.offsetX, boardMetrics.offsetY, boardScale, handleMiniMapPan, nodesById, setSelectedNodeId])

  useEffect(() => {
    const openChecklistItem = (event: Event) => {
      const checklistItemId = (event as CustomEvent<{ checklistItemId?: unknown }>).detail?.checklistItemId
      if (typeof checklistItemId !== 'string' || !checklistItemId) return
      if (openChecklistItemNode(checklistItemId)) {
        pendingOpenChecklistItemIdRef.current = null
      } else {
        pendingOpenChecklistItemIdRef.current = checklistItemId
      }
    }

    window.addEventListener('timelining:open-checklist-item', openChecklistItem)
    return () => window.removeEventListener('timelining:open-checklist-item', openChecklistItem)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: pendingOpenChecklistItemIdRef are useRef/useState identities passed from the Surface (stable)
  }, [openChecklistItemNode])

  useEffect(() => {
    const checklistItemId = pendingOpenChecklistItemIdRef.current
    if (!checklistItemId) return
    if (openChecklistItemNode(checklistItemId)) {
      pendingOpenChecklistItemIdRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: pendingOpenChecklistItemIdRef are useRef/useState identities passed from the Surface (stable)
  }, [displayNodes, openChecklistItemNode])

  const lockedNodeIds = useMemo(() => {
    const locked = new Set<string>()

    for (const node of displayNodes) {
      // Gate nodes are never "locked" themselves — they only relay state.
      if (node.nodeKind === 'GATE_AND' || node.nodeKind === 'GATE_OR') continue

      const externalBlockerId = node.blockedByExternalId ?? node.blockedByExternal?.targetId ?? null
      const externalBlocker = externalBlockerId ? elementById.get(externalBlockerId) : null
      const interWorkspaceProgress = node.interWorkspaceTargetId
        ? externalWorkspaceProgressByToken.get(node.interWorkspaceTargetId) ?? 0
        : 100
      const periodKey = recurringLens ? recurringLens.focusedKey : (node.checklistType === 'WEEKLY' ? weekKey : todayIso)
      const lockState = lockMap.get(`${node.id}:${periodKey}`)
      const isLocked = lockState ? (lockState.isLocked ?? lockState.isBlocked) : false
      if (
        isLocked ||
        (externalBlocker && externalBlocker.progress < 100) ||
        interWorkspaceProgress < 100
      ) locked.add(node.id)
    }

    return locked
  }, [displayNodes, elementById, externalWorkspaceProgressByToken, lockMap, recurringLens, todayIso, weekKey])

  const clientToWorld = useCallback((event: { clientX: number; clientY: number }) => {
    const board = boardRef.current
    const rect = board?.getBoundingClientRect()
    if (!rect || !board) return { x: 0, y: 0 }

    // The world container is sized at `metrics * boardScale` but its children
    // are positioned in unscaled world units (because `transform: scale()` is
    // applied to the inner world). Convert screen-space → scaled-screen-space
    // → unscaled-world by dividing the scroll-adjusted point by boardScale,
    // then subtracting the board's centre offset.
    return {
      x: (event.clientX - rect.left + board.scrollLeft) / boardScale - boardMetrics.offsetX,
      y: (event.clientY - rect.top + board.scrollTop) / boardScale - boardMetrics.offsetY,
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: boardRef are useRef/useState identities passed from the Surface (stable)
  }, [boardMetrics.offsetX, boardMetrics.offsetY, boardScale])

  const flushCursorPresence = () => {
    if (!currentUser || !latestCursorPayloadRef.current) return
    const payload = latestCursorPayloadRef.current
    void setDoc(doc(firestoreDb, 'workspaces', item.workspaceId, 'cursors', currentUser.uid), {
      userId: currentUser.uid,
      userName: currentDisplayName || currentUser.email || 'Teammate',
      userColor: getCursorColor(currentUser.uid),
      canvasX: payload.canvasX,
      canvasY: payload.canvasY,
      activeFocusedNodeId: payload.activeFocusedNodeId,
      lastUpdated: new Date().toISOString(),
    }, { merge: true }).catch((error) => {
      console.warn('[ChecklistDrawingBoard] cursor presence write failed', error)
    })
  }

  // Teammate cursor presence is disabled for now (no real-time presence on the
  // checklist canvas). Kept as a no-op so call sites in the pointer handlers
  // stay intact for a future re-enable.
  const captureCursorPresence = () => {
    void flushCursorPresence
  }

  const showFlash = (message: string) => {
    setFlashMessage(message)
    window.setTimeout(() => setFlashMessage(null), 1400)
  }

  const refreshMarkdownSyncMetadata = () => {
    if (!markdownMetadata.repoPath) return
    setMarkdownMetadata((current) => ({
      ...current,
      lastSyncedCommit: Math.random().toString(16).slice(2, 9),
      isSyncingInProgress: false,
    }))
  }

  return {
    initialVisionboardItems,
    visionboardChecklistItems,
    setVisionboardChecklistItems,
    hydratedVisionboardRef,
    boardMetrics,
    miniMapItems,
    syncCanvasTransformFromScroll,
    handleMiniMapPan,
    openChecklistItemNode,
    lockedNodeIds,
    clientToWorld,
    flushCursorPresence,
    captureCursorPresence,
    showFlash,
    refreshMarkdownSyncMetadata,
  }
}
